// src/components/ItemTooltip.js
import React, { useState, useRef, useEffect } from 'react';
import styles from './ItemTooltip.module.css';
import { ItemData } from '../data/ItemData';
import { getItemImageSrc } from '../utils/getItemImageSrc';
import tooltipManager from '../utils/tooltipManager';
import TooltipPortal from '../utils/TooltipPortal';

const ItemTooltip = ({ 
  children, 
  itemKey, 
  itemInstance = null, 
  showStats = true,
  disabled = false,
  onEquipItem = null,
  onUseItem = null,
  onSellItem = null,
  canEquip = true,
  canUse = true,
  canSell = true
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipId = useRef(`item-tooltip-${Math.random().toString(36).substr(2, 9)}`);

  // Register with tooltip manager
  useEffect(() => {
    const hideTooltip = () => setIsVisible(false);
    tooltipManager.register(tooltipId.current, hideTooltip);
    
    return () => {
      tooltipManager.unregister(tooltipId.current);
    };
  }, []);

  // Close tooltip when clicking outside or pressing escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        tooltipManager.hide(tooltipId.current);
        setIsVisible(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        tooltipManager.hide(tooltipId.current);
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      
      // Safety timeout to auto-hide tooltip after 15 seconds (desktop) or keep mobile timeout
      const autoHideTimeout = setTimeout(() => {
        setIsVisible(false);
      }, window.innerWidth <= 768 ? 5000 : 15000);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
        clearTimeout(autoHideTimeout);
      };
    }
  }, [isVisible]);

  if (disabled || !itemKey) {
    return children;
  }

  const baseItemData = ItemData[itemKey];
  if (!baseItemData) {
    return children;
  }

  const handleMouseEnter = (e) => {
    if (window.innerWidth > 768) { // Only show on hover for desktop
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        updatePosition(e);
        tooltipManager.show(tooltipId.current);
        setIsVisible(true);
      }, 10);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 768) {
      tooltipManager.hide(tooltipId.current);
      setIsVisible(false);
    }
  };

  const handleTouchStart = (e) => {
    // Safely prevent default touch behavior if possible
    if (e.cancelable && e.preventDefault) {
      e.preventDefault();
    }
    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      updatePosition(e.touches[0]);
      tooltipManager.show(tooltipId.current);
      setIsVisible(true);
      
      // Auto-hide after 3 seconds on mobile
      setTimeout(() => {
        tooltipManager.hide(tooltipId.current);
        setIsVisible(false);
      }, 3000);
    }, 10);
  };

  const updatePosition = (event) => {
    if (!triggerRef.current) {
      console.warn('ItemTooltip: triggerRef is null');
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    
    // Validate that we got a proper bounding rect
    if (!rect || rect.width === 0 || rect.height === 0) {
      console.warn('ItemTooltip: Invalid bounding rect', rect);
      // Use mouse position as fallback if available
      if (event && event.clientX && event.clientY) {
        setPosition({ 
          x: event.clientX, 
          y: event.clientY - 50, 
          showBelow: false 
        });
      }
      return;
    }

    // Dynamic tooltip sizing based on screen size
    const isMobile = window.innerWidth <= 768;
    const tooltipWidth = isMobile ? Math.min(300, window.innerWidth - 20) : Math.min(380, window.innerWidth - 20);
    const tooltipHeight = 220; // Approximate tooltip height (taller for stats + buttons)
    const margin = 10; // Margin from screen edges
    
    let x = rect.left + rect.width / 2;
    let y = rect.top - margin;
    let showBelow = false;

    // Check if there's enough space above the item
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    
    if (spaceAbove < tooltipHeight + margin && spaceBelow > tooltipHeight + margin) {
      // Not enough space above, show below
      y = rect.bottom + margin;
      showBelow = true;
    } else if (spaceAbove < tooltipHeight + margin && spaceBelow < tooltipHeight + margin) {
      // Not enough space above OR below, choose the side with more space
      if (spaceBelow > spaceAbove) {
        y = rect.bottom + margin;
        showBelow = true;
      } else {
        // Keep above but adjust to fit in screen
        y = Math.max(margin, rect.top - tooltipHeight - margin);
      }
    }
    // If there's enough space above (default case), keep y as is

    // Adjust horizontal position if tooltip goes off screen
    const leftEdge = x - tooltipWidth / 2;
    const rightEdge = x + tooltipWidth / 2;
    
    if (rightEdge > window.innerWidth - margin) {
      x = window.innerWidth - tooltipWidth / 2 - margin;
    }
    if (leftEdge < margin) {
      x = tooltipWidth / 2 + margin;
    }

    // Final boundary enforcement - absolutely ensure tooltip stays in viewport
    x = Math.max(tooltipWidth / 2 + margin, Math.min(x, window.innerWidth - tooltipWidth / 2 - margin));
    y = Math.max(margin, Math.min(y, window.innerHeight - tooltipHeight - margin));

    // Final validation - if position is still invalid, use safe fallback
    if (isNaN(x) || isNaN(y)) {
      console.warn('ItemTooltip: Invalid calculated position', { x, y, rect });
      x = Math.max(tooltipWidth / 2 + margin, Math.min((event?.clientX || window.innerWidth / 2), window.innerWidth - tooltipWidth / 2 - margin));
      y = Math.max(margin, Math.min((event?.clientY || window.innerHeight / 2) - tooltipHeight / 2, window.innerHeight - tooltipHeight - margin));
    }

    setPosition({ x, y, showBelow });
  };

  const handleEquipClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onEquipItem && canEquip) {
      onEquipItem(itemInstance?.id || itemKey);
    }
    tooltipManager.hide(tooltipId.current);
    setIsVisible(false);
  };

  const handleUseClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onUseItem && canUse) {
      onUseItem(itemInstance?.id || itemKey);
    }
    tooltipManager.hide(tooltipId.current);
    setIsVisible(false);
  };

  const handleSellClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onSellItem && canSell) {
      onSellItem(itemInstance?.id || itemKey);
    }
    tooltipManager.hide(tooltipId.current);
    setIsVisible(false);
  };

  const isEquippable = baseItemData.slotType && typeof itemInstance === 'object';
  const isUsable = baseItemData.type === 'consumable';
  const isSellable = onSellItem && baseItemData.goldValue > 0;
  const isBroken = itemInstance && typeof itemInstance === 'object' && 
    itemInstance.durability !== undefined && itemInstance.durability <= 0;

  const renderTooltipContent = () => {
    return (
      <div className={styles.tooltipContent}>
        <div className={styles.tooltipHeader}>
          <img 
            src={getItemImageSrc(itemKey)} 
            alt={baseItemData.name} 
            className={styles.tooltipIcon}
          />
          <div className={styles.tooltipTitle}>
            <span className={styles.itemName}>{baseItemData.name}</span>
            {itemInstance && typeof itemInstance === 'number' && itemInstance > 1 && (
              <span className={styles.itemQuantity}>x{itemInstance}</span>
            )}
            {itemInstance && typeof itemInstance === 'object' && itemInstance.quantity > 1 && (
              <span className={styles.itemQuantity}>x{itemInstance.quantity}</span>
            )}
          </div>
        </div>
        
        <div className={styles.tooltipDescription}>
          {baseItemData.description || 'No description available.'}
        </div>

        <div className={styles.tooltipStats}>
          {baseItemData.type && (
            <div className={styles.statRow}>
              <span>Type:</span> <span>{baseItemData.type}</span>
            </div>
          )}
          {baseItemData.goldValue !== undefined && (
            <div className={styles.statRow}>
              <span>Value:</span> <span>{baseItemData.goldValue} G</span>
            </div>
          )}
          {baseItemData.weight !== undefined && (
            <div className={styles.statRow}>
              <span>Weight:</span> <span>{baseItemData.weight}</span>
            </div>
          )}
          {itemInstance && typeof itemInstance === 'object' && itemInstance.durability !== undefined && (
            <div className={styles.statRow}>
              <span>Durability:</span> 
              <span className={itemInstance.durability <= 0 ? styles.broken : ''}>
                {itemInstance.durability}/{itemInstance.maxDurability}
              </span>
            </div>
          )}
        </div>

        {(isEquippable || isUsable || isSellable) && (onEquipItem || onUseItem || onSellItem) && (
          <div className={styles.tooltipActions}>
            {isUsable && onUseItem && canUse && (
              <button
                className={`${styles.tooltipButton} ${styles.useButton}`}
                onClick={handleUseClick}
              >
                Use
              </button>
            )}
            {isEquippable && onEquipItem && canEquip && !isBroken && (
              <button
                className={`${styles.tooltipButton} ${styles.equipButton}`}
                onClick={handleEquipClick}
                disabled={isBroken}
              >
                Equip
              </button>
            )}
            {isSellable && canSell && (
              <button
                className={`${styles.tooltipButton} ${styles.sellButton}`}
                onClick={handleSellClick}
              >
                Sell
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        className={styles.tooltipTrigger}
      >
        {children}
      </div>
      
      <TooltipPortal isVisible={isVisible}>
        <div
          ref={tooltipRef}
          className={`${styles.tooltip} ${position.showBelow ? styles.tooltipBelow : ''}`}
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: position.showBelow 
              ? 'translateX(-50%)' 
              : 'translateX(-50%) translateY(-100%)'
          }}
        >
          {renderTooltipContent()}
          <div className={`${styles.tooltipArrow} ${position.showBelow ? styles.arrowUp : styles.arrowDown}`} />
        </div>
      </TooltipPortal>
    </>
  );
};

export default ItemTooltip; 